import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import BackButton from '../../Shared/BackButton'
import { FaFileExport } from "react-icons/fa"
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const DailyReadingDetail = () => {
    const [reading, setReading] = useState([]);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [title, setTitle] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const fetchData = async (payload) => {
            setTitle(`${payload.name}, Reading Report From ${payload.fdate} To ${payload.tdate}`)
            setIsLoading(true)
            try {
                const response = await Server.get(`/reports/${payload.by}-reading/?fdate=${payload.fdate}&tdate=${payload.tdate}&public_id=${payload.public_id}`);
                setReading(response?.data);
                setIsLoading(false);

                const output = response?.data.reduce((data, row) => {
                    data.previous_balance += parseFloat(row.previous_balance);
                    data.difference += parseFloat(row.difference);
                    data.debit += parseFloat(row.debit);
                    return data;
                }, {customers: 0, previous_balance: 0.00, difference: 0, debit: 0.00, total: 0.00});
                setPinnedBottomRowData([
                    {
                        previous_balance: parseFloat(output.previous_balance).toFixed(2),
                        difference: parseFloat(output.difference).toFixed(2),
                        debit: parseFloat(output.debit).toFixed(2),
                        total: (output.previous_balance + output.debit).toFixed(2)
                    }
                ]);
            } catch (error) {
                setIsLoading(false)
                if(error?.response?.status === 401){
                    localStorage.removeItem('user')
                    navigate('/login')
                }else if (error?.response?.status === 403){
                    toast.error(error?.response?.data?.detail || 'Forbidden');
                    navigate('/');
                }else {
                    toast.error(error?.response?.data?.detail || 'Failed to load reading details')
                }
            }
        }

        if (location.state){
            const {data} = location.state
            fetchData({
                fdate: data.fdate,
                tdate: data.tdate,
                public_id: data.public_id,
                by: data.by,
                name: data.name,
            })
        }
    }, []);

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },

        {
            headerName: "Date",
            field: "date",
            sortable: true,
            filter: 'agDateColumnFilter',
            filterParams: {
                comparator: (filterDate, cellValue) => {
                  const cellDate = new Date(cellValue);
                  cellDate.setHours(0, 0, 0, 0);
                  filterDate.setHours(0, 0, 0, 0);

                  if (cellDate < filterDate) return -1;
                  if (cellDate > filterDate) return 1;
                  return 0;
                },
            },
        },
        {
            headerName: "PID",
            field: "previous_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "ID",
            field: "location_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Name",
            field: "full_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Remark",
            field: "remark",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Contact",
            field: "contact_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Prev.Reading",
            field: "previous_reading",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Curr.Reading",
            field: "current_reading",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Difference",
            field: "difference",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Prev.Balance",
            field: "previous_balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Bill",
            field: "debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Total",
            field: "total",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Rate",
            field: "rate_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Village",
            field: "village_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Zone",
            field: "zone_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Station",
            field: "station_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Line",
            field: "line_name",
            sortable: true,
            filter: true,
        },
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi

    const onGridReady=(params)=>{
        gridApi=params.api
    }
      const onExportClick=()=>{
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    const onFilterChanged = (params) => {
        // Get only the filtered data
        const filteredRows = [];
        params.api.forEachNodeAfterFilter(node => filteredRows.push(node.data));

        // Reduce filtered data
        const output = filteredRows.reduce((data, row) => {
            data.previous_balance += Number(row.previous_balance) || 0;
            data.difference += Number(row.difference) || 0;
            data.debit += Number(row.debit) || 0;
            return data;
        }, {customers: 0, previous_balance: 0.00, difference: 0.00, debit: 0.00});

        // Update pinned bottom row with filtered values
        setPinnedBottomRowData([
            {
                previous_balance: output.previous_balance.toFixed(0),
                difference: output.difference.toFixed(2),
                debit: output.debit.toFixed(2),
            }
        ]);

    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <div className="row">
            <div className="col-md-6">
                <BackButton
                    to="/daily-reading-summary"
                    state={{ fdate: location.state?.data?.fdate, tdate: location.state?.data?.tdate, by: location.state?.data?.by }}
                    label="Back to Summary"
                />
            </div>
             <div className="col-md-6">
                <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
            </div>
        </div>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            defaultColDef={defaultColDef}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            onFilterChanged={onFilterChanged}
                            suppressMenuHide={true}

                            rowData={reading}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default DailyReadingDetail